import { faExclamationCircle, faInfoCircle } from '@fortawesome/pro-solid-svg-icons';
import { HoverIconButton } from 'components/button/HoverIconButton';
import { useClickStopPropagationRef } from 'custom_hooks/useClickStopPropagationRef';
import { usePopup } from 'custom_hooks/usePopup';
import { ReactNode } from 'react';
import { TriggeredPopup } from 'shared/triggered_popup/TriggeredPopup';
import { useLimitMaxHeightToScreenRef } from 'shared/triggered_popup/useLimitMaxHeightToScreenRef';
import styled from 'styled-components';
import { stopEvent } from 'utils/event';
import { mergeRefs } from 'utils/mergeRefs';

const ContentContainer = styled.div`
    max-width: 30rem;
    font-size: 1.4rem;
`;

const StyledTriggeredPopup = styled(TriggeredPopup)`
    border: 1px solid var(--border-default);
`;

type InfoPopupProps = {
    title: string;
    triggerClassName?: string;
    triggerStyle?: React.CSSProperties;
    children: ReactNode;
    type?: 'info' | 'error';
};

export function InfoPopup({
    title,
    triggerClassName,
    triggerStyle,
    children,
    type,
}: InfoPopupProps) {
    const { triggerRef, popupRef, isOpen, toggleOpen, close } = usePopup({
        closeOnClickOutside: true,
    });

    const limitMaxHeightRef = useLimitMaxHeightToScreenRef();
    const clickRef = useClickStopPropagationRef<HTMLDivElement>();

    const contentRef = mergeRefs(limitMaxHeightRef, clickRef);

    const isErrorType = type === 'error';

    return (
        <>
            <HoverIconButton
                ref={triggerRef}
                title={title}
                ariaLabel={title}
                icon={isErrorType ? faExclamationCircle : faInfoCircle}
                hoverIcon={isErrorType ? faExclamationCircle : faInfoCircle}
                color={isErrorType ? 'var(--error-default)' : 'var(--text-secondary)'}
                hoverColor={isErrorType ? 'var(--error-hover)' : 'var(--text-link)'}
                onClick={(e) => {
                    stopEvent(e);
                    toggleOpen();
                }}
                className={triggerClassName}
                style={triggerStyle}
            />
            {isOpen && (
                <StyledTriggeredPopup
                    ref={popupRef}
                    anchorRef={triggerRef}
                    onClose={close}
                    test-id="InfoPopup__Popup"
                >
                    <ContentContainer ref={contentRef}>{children}</ContentContainer>
                </StyledTriggeredPopup>
            )}
        </>
    );
}
